import { useState, type FormEvent } from "react";
import { useAuthStore } from "@/stores/authStore";
import { authLinkFailed } from "@/lib/supabase";

type Mode = "sign-in" | "reset";

export function SignInScreen() {
  const [mode, setMode] = useState<Mode>(authLinkFailed ? "reset" : "sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const { signIn, sendPasswordReset, loading, error } = useAuthStore();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setResetSent(false);
    try {
      if (mode === "sign-in") await signIn(email, password);
      else {
        await sendPasswordReset(email);
        setResetSent(true);
      }
    } catch {
      /* error is surfaced via authStore.error */
    }
  }

  return (
    <div className="min-h-[calc(100vh-40px)] flex items-center justify-center px-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-bg1 border border-border rounded-lg shadow-card p-7 space-y-4 animate-fade-up"
      >
        <div className="flex items-baseline gap-2 mb-2">
          <h1 className="text-2xl font-bold tracking-tight text-fg">Neo.</h1>
          <span className="text-[10px] font-mono uppercase tracking-widest text-teal">
            next
          </span>
        </div>

        <div className="flex gap-1 text-xs">
          <Tab label="Sign in"  active={mode === "sign-in"} onClick={() => setMode("sign-in")} />
          <Tab label="Reset"    active={mode === "reset"}   onClick={() => setMode("reset")} />
        </div>

        <Field
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />

        {mode !== "reset" && (
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />
        )}

        {authLinkFailed && !resetSent && (
          <div className="text-xs text-fg bg-bg2 border border-border rounded px-3 py-2">
            That invite or reset link has expired or was already used. Enter your
            email to get a new one, or ask your Neo admin to resend the invite.
          </div>
        )}
        {error && (
          <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">
            {error}
          </div>
        )}
        {resetSent && (
          <div className="text-xs text-success bg-success/10 border border-success/30 rounded px-3 py-2">
            If an account exists for that email, a reset link has been sent.
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-navy text-white rounded py-2 text-sm font-semibold hover:bg-navy-light transition disabled:opacity-60"
        >
          {loading ? "Working…" : mode === "sign-in" ? "Sign in" : "Send reset link"}
        </button>

        <p className="text-[11px] text-fg-dim text-center">
          Accounts are by invitation. Ask your Neo admin for access.
        </p>
      </form>
    </div>
  );
}

const PROMPT_COPY = {
  invite: { title: "Welcome to Neo", hint: "Choose a password to finish setting up your account." },
  recovery: { title: "Set a new password", hint: "Choose a new password for your account." },
  change: { title: "Change password", hint: "Other devices signed in to this account will be signed out." },
} as const;

/**
 * Shown after an invite or password-reset link signs the user in, and from
 * the user menu's "Change password". Those links log the user in without a
 * password, so this is the only place one gets set.
 */
export function SetPasswordScreen() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);

  const { user, passwordPrompt, updatePassword, setPasswordPrompt, signOut, loading, error } =
    useAuthStore();
  const copy = PROMPT_COPY[passwordPrompt ?? "change"];

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    try {
      await updatePassword(password);
    } catch {
      /* error is surfaced via authStore.error */
    }
  }

  return (
    <div className="min-h-[calc(100vh-40px)] flex items-center justify-center px-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-bg1 border border-border rounded-lg shadow-card p-7 space-y-4 animate-fade-up"
      >
        <div>
          <h1 className="text-xl font-bold tracking-tight text-fg">{copy.title}</h1>
          <p className="text-xs text-fg-muted mt-1">{copy.hint}</p>
          {user?.email && <p className="text-xs text-fg-dim mt-1 truncate">{user.email}</p>}
        </div>

        {/* Lets password managers file the new password under this account. */}
        <input type="email" value={user?.email ?? ""} autoComplete="username" readOnly hidden />

        <Field
          label="New password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          minLength={8}
        />
        <Field
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
          minLength={8}
        />

        {(mismatch || error) && (
          <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">
            {mismatch ? "The two passwords don't match." : error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-navy text-white rounded py-2 text-sm font-semibold hover:bg-navy-light transition disabled:opacity-60"
        >
          {loading ? "Saving…" : "Save password"}
        </button>

        <button
          type="button"
          onClick={() => (passwordPrompt === "change" ? setPasswordPrompt(null) : void signOut())}
          className="w-full text-xs text-fg-muted hover:text-fg transition"
        >
          {passwordPrompt === "change" ? "Cancel" : "Not you? Sign out"}
        </button>
      </form>
    </div>
  );
}

function Tab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded border transition ${
        active
          ? "bg-navy text-white border-navy"
          : "bg-bg1 text-fg-muted border-border hover:text-fg"
      }`}
    >
      {label}
    </button>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  ...rest
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "type">) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted mb-1 block">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-bg1 border border-border rounded px-3 py-2 text-sm text-fg outline-none focus:border-teal focus:ring-1 focus:ring-teal/40"
        {...rest}
      />
    </label>
  );
}
